/* eslint-disable react/prop-types */
/**
 * DPROnePageDashboard — in-app dashboard matching the backend one-page PDF.
 * Data source: GET /dpr-log/one-page-dashboard?dprLogId={id}&shift=both
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { message } from 'antd';
import { Button, Spinner } from '@crc-evans/ui-components';
import { RefreshCw, Download, FileText } from 'lucide-react';
import logoCrc from '../../../assets/logoCrc.png';
import { isOffshoreProject } from '../../../utils/projectConfig';
import {
  DPR_REPORT_STATUS,
  ONE_PAGE_UI_REPORT_TYPE,
  formatDprReportGeneratedLabel,
  getStoredDprReportEntry,
  isReportReadyForDownload,
  openOrGenerateOnePageDpr,
} from '../../../services/dprReport';
import {
  fetchOnePageDashboard,
  normalizeOnePageDashboardPayload,
  extractOffshoreCumulativeSummaryFromDashboard,
  hasOffshoreCumulativeSummary,
} from '../../../services/dprOnePageReportView';
import { getMainlineDisplayTitle } from '../ProductivityMainlinePanel';
import { getDprReportFilename, triggerReportDownload } from '../../../utils/reportDownloadUrl';
import {
  buildDprReportContextParams,
  ONGOING_PIPELINE_REQUIRED_MESSAGE,
  showOngoingPipelineSelectForStatus,
} from '../../../utils/dprProjectStatus';
import { arcDashArray, LARGE_ARC_LEN, SMALL_ARC_LEN } from './arcGauge';
import './DPROnePageDashboard.css';

/* ── helpers ────────────────────────────────────────────────────────────── */
const d = (val, fb = '—') => (val == null || val === '' ? fb : String(val));

/** Prefer API text, but treat blank / em-dash placeholders as missing. */
const labelOr = (val, fallback) => {
  if (val == null) return fallback;
  const s = String(val).trim();
  if (!s || s === '—' || s === '-') return fallback;
  return s;
};

/** Foot under reject gauges — "Based on NDT Reject joints …" (not the heading). */
const rejectJointsCaption = (val, fallback) => {
  const s = labelOr(val, fallback);
  if (/accepted/i.test(s)) return fallback;
  if (/rejects/i.test(s) && !/based on/i.test(s)) return fallback;
  return s;
};

/** Offshore today-quality metrics — always show 0 when empty. */
const qualTodayVal = (val) => (val == null || val === '' ? '0' : String(val));

const OFFSHORE_QUALITY_TODAY_CARDS = [
  { key: 'qualCutOutStn1', label: 'Reweld in St1' },
  { key: 'qualNdtRepair', label: 'NDT Repair', labelFromApi: 'qualRepairLabel' },
  { key: 'qualNdtCutOut', label: 'NDT Cut Out' },
];

const PROG_TODAY_CARDS = [
  { valueKey: 'progTodayStn1', labelKey: 'progTodayStn1Label', fallbackLabel: 'St1 Welds' },
  { valueKey: 'progTodayNdtCompleted', labelKey: 'progTodayNdtCompletedLabel', fallbackLabel: 'NDT Completed' },
  { valueKey: 'progTodayNdtAccepted', labelKey: 'progTodayNdtAcceptedLabel', fallbackLabel: 'NDT Accepted' },
];

function SectionLabel({ children, color }) {
  return (
    <div className="op-sl" style={color ? { background: color } : undefined}>
      {children}
    </div>
  );
}

function SectionWrap({ children, noPad }) {
  return <div className={`op-sw${noPad ? ' op-sw--np' : ''}`}>{children}</div>;
}

/* ── gauge SVGs ─────────────────────────────────────────────────────────── */
function LargeArcGauge({ pct, color = '#087e8b', arcDash, arcLen }) {
  const pctNum = parseFloat(pct) || 0;
  const dash = arcDash ?? arcDashArray(pctNum, LARGE_ARC_LEN).dash;
  const len = arcLen ?? LARGE_ARC_LEN;
  return (
    <svg width="106" height="54" viewBox="0 0 106 54" className="op-gauge-svg">
      <path d="M8,48 A45,45 0 0,1 98,48" fill="none" stroke="#dde1e7" strokeWidth="10" strokeLinecap="round" />
      <path
        d="M8,48 A45,45 0 0,1 98,48"
        fill="none"
        stroke={color}
        strokeWidth="10"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${len}`}
      />
      <text x="53" y="40" textAnchor="middle" fontFamily="Segoe UI,Arial" fontSize="11" fontWeight="900" fill="#2b2d42">
        {d(pct, '0')} %
      </text>
    </svg>
  );
}

function SmallArcGauge({ pct, color = '#087e8b', arcDash, arcLen, capLabel }) {
  const pctNum = parseFloat(pct) || 0;
  const dash = arcDash ?? arcDashArray(pctNum, SMALL_ARC_LEN).dash;
  const len = arcLen ?? SMALL_ARC_LEN;
  return (
    <svg width="82" height="48" viewBox="0 0 82 48" className="op-gauge-svg">
      <path d="M8,41 A33,33 0 0,1 74,41" fill="none" stroke="#dde1e7" strokeWidth="9" strokeLinecap="round" />
      <path
        d="M8,41 A33,33 0 0,1 74,41"
        fill="none"
        stroke={color}
        strokeWidth="9"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${len}`}
      />
      {capLabel ? (
        <>
          <circle cx="74" cy="41" r="2.25" fill="#c81d25" />
          <text x="77" y="40" textAnchor="start" fontFamily="Segoe UI,Arial" fontSize="5.5" fontWeight="700" fill="#c81d25">
            {capLabel}%
          </text>
        </>
      ) : null}
      <text x="41" y="34" textAnchor="middle" fontFamily="Segoe UI,Arial" fontSize="8.5" fontWeight="900" fill="#2b2d42">
        {d(pct, '0')} %
      </text>
    </svg>
  );
}

function ConsModSemicircleGauge({
  heroVal,
  barColor = '#087e8b',
  consUsedArcDash,
  consArcLen,
  scaleMax,
  showLimitMarker,
  limitMarkerX,
  limitMarkerY,
  showNeedMarker,
  needMarkerX,
  needMarkerY,
}) {
  const dash = consUsedArcDash ?? arcDashArray(0, SMALL_ARC_LEN).dash;
  const len = consArcLen ?? SMALL_ARC_LEN;
  return (
    <div className="op-cons-mod-g">
      <svg className="op-cons-mod-svg" viewBox="0 0 82 48" preserveAspectRatio="xMidYMid meet" aria-hidden>
        <path d="M8,41 A33,33 0 0,1 74,41" fill="none" stroke="#dde1e7" strokeWidth="7" strokeLinecap="round" />
        <path
          d="M8,41 A33,33 0 0,1 74,41"
          fill="none"
          stroke={barColor}
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${len}`}
        />
        {showLimitMarker && limitMarkerX != null && limitMarkerY != null ? (
          <circle cx={limitMarkerX} cy={limitMarkerY} r="2" fill="#c81d25" />
        ) : null}
        {showNeedMarker && needMarkerX != null && needMarkerY != null ? (
          <circle cx={needMarkerX} cy={needMarkerY} r="2" fill="#c81d25" />
        ) : null}
        <text x="41" y="33" textAnchor="middle" fontFamily="Segoe UI,Arial,sans-serif" fontSize="10" fontWeight="900" fill="#2b2d42">
          {d(heroVal, '0')}
        </text>
      </svg>
      {scaleMax != null ? (
        <div className="op-cons-mod-scale">
          <span>0</span>
          <span>{scaleMax}</span>
        </div>
      ) : null}
    </div>
  );
}

function formatWeeklyLimitLabel(item) {
  if (item?.showWeeklyLimitLbl === false) return null;
  const val = item?.weeklyLimitVal;
  const base = val != null && val !== '' ? `Weekly Limit ${val}` : 'Weekly Limit';
  if (!item?.isGas) return base;
  const suffix = item.gasConsumptionUnitSuffix
    ?? (item.gasConsumptionUnitTitle ? ` ${item.gasConsumptionUnitTitle}` : '');
  return `${base}${suffix || ''}`;
}

function ConsModUsageFooter({ item, todayVal, gasSuffix = '' }) {
  const remainingLabel = item?.remainingLabel || 'REMAINING';
  const availableNote = item?.availableNote;
  const consumptionTotal = item?.consumptionTotal ?? '0';
  const consumptionUnit = item?.consumptionUnit
    || (item?.isGas ? (item.gasConsumptionUnitTitle || 'm³') : 'bag');
  const todayLine = item?.todayUsageLine
    || (item?.isGas
      ? `Today usage: ${todayVal != null ? todayVal : '0'}${gasSuffix}`
      : `Today usage: ${todayVal != null ? todayVal : '0'} Bags`);
  return (
    <>
      <div className="op-cons-mod-lbl">{d(remainingLabel)}</div>
      {availableNote ? (
        <div className="op-cons-mod-needlbl">{d(availableNote)}</div>
      ) : null}
      <div className="op-cons-mod-nums">Consumption Total</div>
      <div className="op-cons-mod-nums">
        {d(consumptionTotal, '0')} {d(consumptionUnit, 'bag')}
      </div>
      <div className="op-cons-mod-nums">{d(todayLine)}</div>
    </>
  );
}

function ConsModLeftGauge({ item }) {
  const todayVal = pickConsumableToday(item);
  const gasSuffix = item.isGas
    ? (item.gasConsumptionUnitSuffix
      ?? (item.gasConsumptionUnitTitle ? ` ${item.gasConsumptionUnitTitle}` : ''))
    : '';
  const weeklyLimitLabel = formatWeeklyLimitLabel(item);
  return (
    <div className="op-cons-mod">
      <div className="op-cons-mod-name">{d(item.title ?? item.name)}</div>
      {weeklyLimitLabel ? (
        <div className="op-cons-mod-wklim">{weeklyLimitLabel}</div>
      ) : null}
      {item.badge ? (
        <div className="op-cons-mod-meta">
          <span className={`op-cons-badge op-cons-badge--${item.badgeClass ?? 'ok'}`}>{d(item.badge)}</span>
        </div>
      ) : null}
      <ConsModSemicircleGauge
        heroVal={item.gaugeHeroVal}
        barColor={item.barColorUsed ?? '#087e8b'}
        consUsedArcDash={item.consUsedArcDash}
        consArcLen={item.consArcLen}
        scaleMax={item.scaleMax}
        showLimitMarker={item.showLimitMarker}
        limitMarkerX={item.limitMarkerX}
        limitMarkerY={item.limitMarkerY}
      />
      <ConsModUsageFooter item={item} todayVal={todayVal} gasSuffix={gasSuffix} />
    </div>
  );
}

function ConsModSuppliedVsNeeded({ wireTitle, item, unitNote }) {
  if (!item) return null;
  return (
    <div className="op-cons-mod">
      <div className="op-cons-mod-name">{d(wireTitle, 'PIH1')} — REMAINING STOCK</div>
      {unitNote ? <div className="op-cons-mod-lbl">{unitNote}</div> : null}
      <ConsModSemicircleGauge
        heroVal={item.gaugeHeroVal}
        barColor={item.barColorUsed ?? '#087e8b'}
        consUsedArcDash={item.consUsedArcDash}
        consArcLen={item.consArcLen}
        scaleMax={item.scaleMax}
        showNeedMarker={item.showNeedMarker}
        needMarkerX={item.needMarkerX}
        needMarkerY={item.needMarkerY}
      />
      <ConsModUsageFooter item={{ ...item, remainingLabel: item.remainingLabel || 'REMAINING STOCK' }} />
    </div>
  );
}

function pickConsumableToday(item) {
  const v = item.consumptionToday ?? item.todayUsed ?? item.today ?? item.usedToday;
  if (v != null && v !== '') return v;
  const fromNums = String(item.nums || '').trim().match(/today\s*:?\s*([\d.]+)/i);
  return fromNums ? fromNums[1] : null;
}

/* ── section components ─────────────────────────────────────────────────── */
function HeaderSection({ header, project }) {
  if (!header) return null;
  const crceLogo = header.crceLogoSrc || logoCrc;
  const projectTitle = header.bundleTitle
    ?? header.projectTitle
    ?? project?.projectName
    ?? project?.name;
  return (
    <div className="op-hdr">
      <div className="op-hdr-left">
        <img src={crceLogo} alt="CRC Evans" className="op-crce-logo" />
        {header.clientLogoSrc ? (
          <img
            src={header.clientLogoSrc}
            alt={header.clientTag || 'Client'}
            className="op-client-logo"
          />
        ) : (
          <div className="op-client-tag">{d(header.clientTag, 'Client')}</div>
        )}
      </div>
      <div className="op-hdr-mid">
        <div className="op-hdr-title">Welding Daily Progress Report</div>
        <div className="op-hdr-project">{d(projectTitle, 'Daily Progress Report')}</div>
      </div>
      <div className="op-hdr-right">
        <div className="op-hdr-dpr-lbl">DPR No.</div>
        <div className="op-hdr-dpr-num">{d(header.bundleReportNum)}</div>
      </div>
    </div>
  );
}

const ONE_PAGE_META_FIELDS = [
  ['projectNumber', 'Project Number'],
  ['client', 'Client'],
  ['finalClient', 'Final Client'],
  ['vessel', 'Vessel Name'],
  ['location', 'Location'],
  ['date', 'Date'],
  ['dprProjectStatus', 'Project Status'],
  ['projectStart', 'Project Start'],
  ['expectedProjectLength', 'Expected Project Length (m)'],
  ['expectedProjectWelds', 'Expected Project Welds'],
  ['ndtType', 'NDT type'],
  ['crceRepresentative', 'CRCE Representative'],
  ['ongoingPipe', 'On-going Pipe'],
  ['pipeStart', 'Pipe Start'],
  ['expectedPipeLength', 'Expected Pipe Length (m)'],
  ['expectedPipeWelds', 'Expected Pipe Welds'],
  ['od', 'OD (")'],
  ['wt', 'WT (mm)'],
];

/** Offshore On-going Pipe follows the DPR header selector: it only exists for Production. */
function resolveOngoingPipe({ meta, project, selectedPipeline, status }) {
  if (meta?.suppressPipelineProgress) return '';
  if (!isOffshoreProject(project)) return meta.ongoingPipe;
  if (!showOngoingPipelineSelectForStatus(status)) return '';
  return selectedPipeline ? getMainlineDisplayTitle(selectedPipeline) : meta.ongoingPipe;
}

function MetaSection({ meta, project, selectedPipeline, dprProjectStatus }) {
  if (!meta || typeof meta !== 'object') return null;
  const status = dprProjectStatus || meta.dprProjectStatus || '';
  const blankZero = (val) => {
    if (val == null || val === '') return '';
    const s = String(val).trim();
    return s === '0' ? '' : val;
  };
  const displayMeta = {
    ...meta,
    ongoingPipe: resolveOngoingPipe({ meta, project, selectedPipeline, status }),
    ...(meta.suppressPipelineProgress
      ? { expectedPipeWelds: '', expectedPipeLength: '' }
      : {
          expectedPipeWelds: blankZero(meta.expectedPipeWelds),
          expectedPipeLength: blankZero(meta.expectedPipeLength),
        }),
  };
  return (
    <div className="op-meta">
      {ONE_PAGE_META_FIELDS.map(([key, label]) => (
        <div key={key} className="op-mc">
          <div className="op-mc-l">{label}</div>
          <div className="op-mc-v">{d(displayMeta[key])}</div>
        </div>
      ))}
    </div>
  );
}

function ProgressSection({ progress, meta }) {
  if (!progress || typeof progress !== 'object') return null;
  const pipeFootCaption = progress.progressPipeFootCaption || 'NDT Accepted to date';
  const projectFootCaption = progress.progressProjectFootCaption || 'NDT Accepted to date';
  const ndtAccLabel = progress.ndtAccBoxLabel || 'NDT Accepted';
  const expectedPipeWelds =
    meta?.expectedPipeWelds == null ||
    meta?.expectedPipeWelds === '' ||
    String(meta.expectedPipeWelds).trim() === '0'
      ? ''
      : meta.expectedPipeWelds;
  return (
    <>
      <SectionLabel>Progress</SectionLabel>
      <SectionWrap>
        <div className="op-section-sublbl">{d(progress.progTodayTitle, "Today's Progress (day + night)")}</div>
        <div className="op-qcards op-qcards--progress">
          {PROG_TODAY_CARDS.map(({ valueKey, labelKey, fallbackLabel }) => (
            <div className="op-qcard" key={valueKey}>
              <div className="op-qcard-l">{d(progress[labelKey], fallbackLabel)}</div>
              <div className="op-qcard-v">{qualTodayVal(progress[valueKey])}</div>
            </div>
          ))}
        </div>
        <div className="op-prog">
          <div className="op-gbox">
            <div className="op-glbl">% Pipe Progress</div>
            <LargeArcGauge
              pct={progress.pipeProgPct}
              color="#087e8b"
              arcDash={progress.pipeArcDash}
              arcLen={progress.pipeArcLen}
            />
            <div className="op-gauge-foot">
              {pipeFootCaption}
              <br />
              <strong>{d(progress.progNdtAcceptedSowToDate)}</strong>
              {' / '}
              {d(expectedPipeWelds)}
            </div>
          </div>
          <div className="op-pweld">
            <div className="op-pweld-t">Pipe Welds</div>
            <div className="op-pweld-l">{ndtAccLabel}</div>
            <div className="op-pweld-v">{d(progress.progNdtAcceptedSowToDate)}</div>
          </div>
          <div className="op-gbox">
            <div className="op-glbl">% Project Progress</div>
            <LargeArcGauge
              pct={progress.projProgPct}
              color="#4361ee"
              arcDash={progress.projArcDash}
              arcLen={progress.projArcLen}
            />
            <div className="op-gauge-foot">
              {projectFootCaption}
              <br />
              <strong>{d(progress.progNdtAcceptedProjectToDate)}</strong>
              {' / '}
              {d(meta?.expectedProjectWelds)}
            </div>
          </div>
          <div className="op-pweld">
            <div className="op-pweld-t">Project Welds</div>
            <div className="op-pweld-l">{ndtAccLabel}</div>
            <div className="op-pweld-v">{d(progress.progNdtAcceptedProjectToDate)}</div>
          </div>
        </div>
      </SectionWrap>
    </>
  );
}

function QualitySection({ quality }) {
  if (!quality || typeof quality !== 'object') return null;
  const pipeRejectPct = quality.ndtRejectPctPipeline ?? quality.pipeDefPct;
  const projectRejectPct = quality.ndtRejectPctProject ?? quality.projDefPct;
  return (
    <>
      <SectionLabel>Quality</SectionLabel>
      <SectionWrap>
        <div className="op-quality-stack">
          <div className="op-section-sublbl">Today Quality (day + night)</div>
          <div className="op-qcards">
            {OFFSHORE_QUALITY_TODAY_CARDS.map(({ key, label, labelFromApi }) => (
              <div className="op-qcard" key={key}>
                <div className="op-qcard-l">
                  {labelFromApi ? d(quality[labelFromApi], label) : label}
                </div>
                <div className="op-qcard-v">{qualTodayVal(quality[key])}</div>
              </div>
            ))}
          </div>
          {(quality.ndtQualityNote || quality.rejectPctFormulaNote) ? (
            <div className="op-quality-notes">
              {quality.ndtQualityNote ? <div>{d(quality.ndtQualityNote)}</div> : null}
              {quality.rejectPctFormulaNote ? <div>{d(quality.rejectPctFormulaNote)}</div> : null}
            </div>
          ) : null}
          <div className="op-prog op-prog--quality">
            <div className="op-gbox">
              <div className="op-glbl">
                {labelOr(quality.pipeDefectGaugeLabel, '% NDT Rejects ongoing pipeline')}
              </div>
              <SmallArcGauge
                pct={pipeRejectPct}
                color={quality.pipeDefGaugeColor ?? '#087e8b'}
                capLabel={quality.ndtRejectCapLabel}
              />
              <div className="op-gauge-foot">
                {rejectJointsCaption(
                  quality.pipeDefectGaugeCaption,
                  'Based on NDT Reject joints (ongoing pipeline)'
                )}
              </div>
            </div>
            <div className="op-pweld">
              <div className="op-pweld-t">Ongoing Pipeline</div>
              <div className="op-pweld-l">Total Pipeline Rejects</div>
              <div className="op-pweld-v">{qualTodayVal(quality.totalPipelineRejects)}</div>
            </div>
            <div className="op-gbox">
              <div className="op-glbl">
                {labelOr(quality.projectDefectGaugeLabel, '% NDT Rejects overall project')}
              </div>
              <SmallArcGauge
                pct={projectRejectPct}
                color={quality.projDefGaugeColor ?? '#4361ee'}
                capLabel={quality.ndtRejectCapLabel}
              />
              <div className="op-gauge-foot">
                {rejectJointsCaption(
                  quality.projectDefectGaugeCaptionLine1,
                  'Based on NDT Reject joints (overall project)'
                )}
              </div>
            </div>
            <div className="op-pweld">
              <div className="op-pweld-t">Overall Project</div>
              <div className="op-pweld-l">Total Project Rejects</div>
              <div className="op-pweld-v">{qualTodayVal(quality.totalProjectRejects)}</div>
            </div>
          </div>
        </div>
      </SectionWrap>
    </>
  );
}

function SafetySection({ safety }) {
  if (!safety || typeof safety !== 'object') return null;
  return (
    <>
      <SectionLabel>Safety</SectionLabel>
      <SectionWrap>
        <div className="op-safgrid">
          <div className="op-sp">
            <div className="op-sp-t">Total Incidents</div>
            <div className="op-sub2">
              <div><div className="op-snl">Today</div><div className="op-snv">{d(safety.incToday, '0')}</div></div>
              <div><div className="op-snl">To Date Project</div><div className="op-snv">{d(safety.incToDate, '0')}</div></div>
            </div>
          </div>
          <div className="op-sp">
            <div className="op-sp-t">Lost Time Injury</div>
            <div className="op-sub2">
              <div><div className="op-snl">Today</div><div className="op-snv">{d(safety.ltiToday, '0')}</div></div>
              <div><div className="op-snl">To Date Project</div><div className="op-snv">{d(safety.ltiToDate, '0')}</div></div>
            </div>
          </div>
          <div className="op-sp">
            <div className="op-sp-t">Toolbox Talk</div>
            <div className="op-sub2">
              <div><div className="op-snl">Today</div><div className="op-snv">{d(safety.tbtToday, '0')}</div></div>
              <div><div className="op-snl">To Date Project</div><div className="op-snv">{d(safety.tbtToDate, '0')}</div></div>
            </div>
          </div>
          <div className="op-sp">
            <div className="op-sp-t">Toolbox Talk Comments</div>
            <div className="op-sp-comment">
              {safety.tbtComments && typeof safety.tbtComments !== 'object'
                ? String(safety.tbtComments)
                : ''}
            </div>
          </div>
        </div>
      </SectionWrap>
    </>
  );
}

function DowntimeSection({ downtime }) {
  if (!downtime || typeof downtime !== 'object') return null;
  const rows = Array.isArray(downtime.rows) ? downtime.rows : [];
  return (
    <>
      <SectionLabel>Downtimes [min] — today (day + night combined)</SectionLabel>
      <SectionWrap>
        <div className="op-dt-one">
          {rows.map((row, i) => (
            <div key={row.label ?? i} className="op-dt-pair">
              <span className="op-dt-n">{d(row.label)}</span>
              <strong>{d(row.today)}</strong>
            </div>
          ))}
          <div className="op-dt-pair op-dt-pair--total">
            <span className="op-dt-n">Total today</span>
            <strong>{d(downtime.columnTotals?.today)}</strong>
          </div>
          <div className="op-dt-pair op-dt-pair--dt">
            <span className="op-dt-n">To date</span>
            <strong>{d(downtime.projectToDateMin)}</strong>
            <span className="op-dt-sub">min</span>
          </div>
        </div>
      </SectionWrap>
    </>
  );
}

function ManpowerSection({ manpower, showOffshorePobShiftGrid = false }) {
  if (!manpower || typeof manpower !== 'object') return null;
  const showThreeShiftGrid = showOffshorePobShiftGrid || manpower.showOffshorePobShiftGrid;
  return (
    <>
      <SectionLabel>CRCE Manpower</SectionLabel>
      <SectionWrap noPad>
        <div className="op-mpi op-mpi-full">
          <div className="op-mps">
            <div className="op-mpl">P.O.B. per shift</div>
            <div className={`op-mpr${showThreeShiftGrid ? ' op-mpr-three' : ''}`}>
              <div><div className="op-mpm">Day Shift</div><div className="op-mpn">{d(manpower.pobDay, '0')}</div></div>
              <div><div className="op-mpm">Night Shift</div><div className="op-mpn">{d(manpower.pobNight, '0')}</div></div>
              {showThreeShiftGrid ? (
                <div><div className="op-mpm">6-6 Shift</div><div className="op-mpn">{d(manpower.pobSixSix, '0')}</div></div>
              ) : null}
            </div>
          </div>
          <div className="op-mhb">
            <div className="op-mhl">To Date Project Man Hour</div>
            <div className="op-mhv">{d(manpower.manHoursToDate)}</div>
          </div>
        </div>
      </SectionWrap>
    </>
  );
}

function CriticalConsumablesSection({ criticalConsumables }) {
  if (!criticalConsumables || !criticalConsumables.hasData) {
    return (
      <>
        <SectionLabel>Critical Consumables</SectionLabel>
        <SectionWrap>
          <div className="op-empty-box">No consumables recorded for this report date.</div>
        </SectionWrap>
      </>
    );
  }
  const leftGauges = Array.isArray(criticalConsumables.leftGauges) ? criticalConsumables.leftGauges : [];
  const rw = criticalConsumables.rightWire;

  return (
    <>
      <SectionLabel>Critical Consumables</SectionLabel>
      <SectionWrap noPad style={{ paddingTop: 4, paddingBottom: 4 }}>
        <div className="op-cons-layout-inline">
          {leftGauges.map((item, i) => (
            <ConsModLeftGauge key={item.title ?? item.name ?? i} item={item} />
          ))}
          {rw?.actualVsPlanned ? (
            <div className="op-cons-mod op-cons-mod-wide">
              <div className="op-cons-mod-name">
                {d(rw.title, 'PIH1')} — USED % ACTUAL vs PLANNED
              </div>
              {rw.actualVsPlanned?.showBadge && rw.actualVsPlanned.badge ? (
                <div className="op-cons-mod-meta">
                  <span className={`op-cons-badge op-cons-badge--${rw.actualVsPlanned.badgeClass ?? 'ok'}`}>
                    {d(rw.actualVsPlanned.badge)}
                  </span>
                </div>
              ) : null}
              <div className="op-wire-hbar">
                <div className="op-wire-hbar-row">
                  <div className="op-wire-hbar-lbl">Act</div>
                  <div className="op-wire-hbar-track">
                    <div
                      className="op-wire-hbar-fill op-wire-hbar-fill--actual"
                      style={{ width: `${Math.min(parseFloat(rw.actualVsPlanned.actualBarWidthPct) || 0, 100)}%` }}
                    />
                  </div>
                  <div className="op-wire-hbar-pct">{d(rw.actualVsPlanned.actualPct)}%</div>
                </div>
                <div className="op-wire-hbar-row">
                  <div className="op-wire-hbar-lbl">Plan</div>
                  <div className="op-wire-hbar-track">
                    <div
                      className="op-wire-hbar-fill op-wire-hbar-fill--planned"
                      style={{ width: `${Math.min(parseFloat(rw.actualVsPlanned.plannedBarWidthPct) || 0, 100)}%` }}
                    />
                  </div>
                  <div className="op-wire-hbar-pct">{d(rw.actualVsPlanned.plannedPct)}%</div>
                </div>
              </div>
              <div className="op-wire-hbar-legend">
                <span><span className="op-wire-hbar-legend-dot op-wire-hbar-legend-dot--actual" />Actual</span>
                <span><span className="op-wire-hbar-legend-dot op-wire-hbar-legend-dot--planned" />Planned</span>
              </div>
            </div>
          ) : null}
          {rw?.suppliedVsNeeded ? (
            <ConsModSuppliedVsNeeded
              wireTitle={rw.title}
              item={rw.suppliedVsNeeded}
              unitNote={rw.unitNote}
            />
          ) : null}
          {rw?.remainingDays ? (
            <div className="op-cons-mod">
              <div className="op-cons-mod-name">Remaining Days With Stock</div>
              <div className="op-wire-rem-sub">Based On Avg Consum</div>
              <div className="op-wire-rem-hero">{d(rw.remainingDays.value, '0')}</div>
              <div className="op-cons-mod-nums">
                Avg {d(rw.remainingDays.avgDaily, '0')}/day · stock {d(rw.remainingDays.stock, '0')}
              </div>
            </div>
          ) : null}
        </div>
      </SectionWrap>
    </>
  );
}

function CommentsSection({ comments }) {
  if (!comments || typeof comments !== 'object') return null;
  return (
    <>
      <SectionLabel>General Comments</SectionLabel>
      <SectionWrap>
        <div className="op-comments-two">
          <div className="op-cbox">{d(comments.generalNight, '')}</div>
          <div className="op-cbox">{d(comments.generalDay, '')}</div>
        </div>
      </SectionWrap>
    </>
  );
}

function SignaturesSection({ signatures, hideSubContractor = false }) {
  if (!signatures) return null;
  const sigs = [
    signatures.approvedBy1,
    signatures.approvedBy2,
    hideSubContractor ? null : signatures.approvedBy3,
  ].filter(Boolean);
  if (sigs.length === 0) return null;
  return (
    <>
      <SectionLabel>Signatures</SectionLabel>
      <SectionWrap>
        <div className={`op-siggrid op-siggrid--${sigs.length}`}>
          {sigs.map((sig, i) => (
            <div key={i} className="op-sigc">
              <div className="op-sigp">{d(sig.label)}</div>
              <div className="op-sig-img-wrap">
                {sig.signature ? (
                  <img className="op-sig-img" src={sig.signature} alt="" />
                ) : (
                  <span className="op-sig-ph">—</span>
                )}
              </div>
              <div className="op-sigln">
                <div className="op-sig-name">{d(sig.name)}</div>
                <div className="op-sig-pos">{d(sig.position)}</div>
              </div>
            </div>
          ))}
        </div>
      </SectionWrap>
    </>
  );
}

/* ── main view ──────────────────────────────────────────────────────────── */
const DPROnePageDashboard = ({
  dprLogId,
  shift,
  date,
  reportDetails,
  selectedProject,
  selectedPipeline,
  allPipelines = [],
  ongoingPipelineId = null,
  dprProjectStatus = '',
  requireOngoingPipeline = false,
  productivitySummary = null,
  refreshToken = 0,
  dprLogData = null,
  onReportGenerated,
  onHeaderSummaryLoaded = null,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [dashData, setDashData] = useState(null);

  const [reportEntry, setReportEntry] = useState(() =>
    getStoredDprReportEntry(reportDetails, ONE_PAGE_UI_REPORT_TYPE)
  );
  const [isGenerating, setIsGenerating] = useState(false);

  const pdfUrl = reportEntry?.fileUrl ?? null;
  const formattedDate = moment(date).format('YYYY-MM-DD');
  const generatedLabel = formatDprReportGeneratedLabel(reportEntry);

  const fetchContextRef = useRef({});
  fetchContextRef.current = {
    project: selectedProject,
    pipeline: selectedPipeline,
    dprLog: dprLogData,
    allPipelines,
    date,
    ongoingPipelineId,
    dprProjectStatus: dprProjectStatus || dprLogData?.dprProjectStatus,
  };

  const load = useCallback(async () => {
    if (!dprLogId) {
      setLoadError('DPR log is not loaded yet.');
      setIsLoading(false);
      return;
    }
    const ctx = fetchContextRef.current;
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await fetchOnePageDashboard(dprLogId, {
        shift: 'both',
        project: ctx.project,
        pipeline: ctx.pipeline,
        dprLog: ctx.dprLog,
        allPipelines: ctx.allPipelines,
        date: ctx.date,
        sowId: ctx.pipeline?.id ?? ctx.ongoingPipelineId,
        ongoingPipelineId: ctx.ongoingPipelineId,
        dprProjectStatus: ctx.dprProjectStatus,
      });
      setDashData(data);
      if (typeof onHeaderSummaryLoaded === 'function') {
        const extracted = extractOffshoreCumulativeSummaryFromDashboard(data);
        if (hasOffshoreCumulativeSummary(extracted)) {
          onHeaderSummaryLoaded(extracted);
        }
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404 || status === 501 || status === 503) {
        setLoadError('Dashboard API not yet available. Generate a PDF to preview below.');
      } else {
        setLoadError(err?.message || 'Failed to load one-page dashboard.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    dprLogId,
    selectedProject?.id,
    selectedPipeline?.id,
    ongoingPipelineId,
    dprProjectStatus,
    dprLogData?.id,
    formattedDate,
    onHeaderSummaryLoaded,
  ]);

  useEffect(() => {
    setReportEntry(getStoredDprReportEntry(reportDetails, ONE_PAGE_UI_REPORT_TYPE));
  }, [reportDetails]);

  useEffect(() => { void load(); }, [load, refreshToken]);

  const handleGenerate = async (regenerate = false) => {
    if (!dprLogId) { message.error('DPR log not loaded.'); return; }
    const reportContextParams = buildDprReportContextParams({
      ongoingPipelineId,
      dprProjectStatus: dprProjectStatus || dprLogData?.dprProjectStatus,
      requireOngoingPipeline,
    });
    if (reportContextParams == null) {
      message.error(ONGOING_PIPELINE_REQUIRED_MESSAGE);
      return;
    }
    setIsGenerating(true);
    try {
      const result = await openOrGenerateOnePageDpr({
        dprLogId,
        reportDetails,
        preferRegenerate: regenerate,
        requireOngoingPipeline,
        ...reportContextParams,
      });
      if (result.reportEntry) setReportEntry(result.reportEntry);
      message.success(
        result.openedFromCache ? 'One-page DPR is ready.' :
        result.fileUrl ? 'One-page DPR generated.' :
        'Generation started.'
      );
      if (onReportGenerated) onReportGenerated();
      await load();
    } catch (err) {
      message.error(err?.message || 'Failed to generate one-page DPR.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!pdfUrl) { message.info('Generate the PDF first.'); return; }
    triggerReportDownload(pdfUrl, getDprReportFilename(ONE_PAGE_UI_REPORT_TYPE, formattedDate, shift));
    message.success('Download started.');
  };

  const handleOpenPdf = () => {
    if (!pdfUrl) { message.info('Generate the PDF first.'); return; }
    window.open(pdfUrl, '_blank', 'noopener,noreferrer');
  };

  const hasReportError = reportEntry?.reportStatus === DPR_REPORT_STATUS.ERROR;
  const isReady = reportEntry?.reportStatus === DPR_REPORT_STATUS.GENERATED;
  const primaryLabel = isGenerating
    ? 'Generating…'
    : hasReportError
      ? 'Retry PDF'
      : isReady
        ? 'Regenerate PDF'
        : 'Generate PDF';

  /* ── header derived from normalized dashboard payload ─ */
  const viewModel = useMemo(
    () => (dashData
      ? normalizeOnePageDashboardPayload(dashData, {
        project: selectedProject,
        pipeline: selectedPipeline,
        dprLog: dprLogData,
        allPipelines,
        date,
      })
      : null),
    [dashData, selectedProject, selectedPipeline, dprLogData, allPipelines, date]
  );

  const header = viewModel?.header ?? null;
  const meta = viewModel?.meta ?? null;
  const progress = viewModel?.progress ?? null;
  const quality = viewModel?.quality ?? null;
  const safety = viewModel?.safety ?? null;
  const downtime = viewModel?.downtime ?? null;
  const manpower = viewModel?.manpower ?? null;
  const criticalConsumables = viewModel?.criticalConsumables ?? null;
  const comments = viewModel?.comments ?? null;
  const signatures = viewModel?.signatures ?? null;
  const hasRenderableSections = Boolean(
    viewModel && (
      header
      || progress?.pipeProgPct != null
      || meta?.projectNumber
      || quality?.qualCutOutStn1 != null
      || quality?.qualNdtRepair != null
      || quality?.qualNdtCutOut != null
      || quality?.pipeDefPct != null
      || safety?.incToday != null
      || (Array.isArray(downtime?.rows) && downtime.rows.length > 0)
      || manpower?.pobDay != null
      || criticalConsumables?.hasData
      || (Array.isArray(criticalConsumables?.leftGauges) && criticalConsumables.leftGauges.length > 0)
      || criticalConsumables?.rightWire
    )
  );

  return (
    <div className="dpr-one-page-panel">
      <div className="dpr-one-page-card">
        <div className="dpr-one-page-card-head">
          <div className="dpr-one-page-card-head-left">
            <div className="dpr-one-page-card-icon" aria-hidden>
              <FileText size={24} />
            </div>
            <div>
              <h3 className="dpr-one-page-card-title">One Page Report Summary</h3>
              <p className="dpr-one-page-card-desc">
                Live dashboard view matching the one-page PDF report. Generate or download the PDF without submitting Comments.
              </p>
              {isReady && generatedLabel !== 'A report is already saved for this type.' ? (
                <p className="dpr-one-page-card-meta">{generatedLabel}</p>
              ) : null}
            </div>
          </div>
          <div className="dpr-one-page-card-actions">
            <button
              type="button"
              className="dpr-one-page-icon-btn"
              onClick={load}
              disabled={isLoading}
              title="Refresh"
              aria-label="Refresh one-page dashboard"
            >
              <RefreshCw size={16} />
            </button>
            <Button
              label={primaryLabel}
              variant="primary"
              size="medium"
              onClick={() => handleGenerate(isReady || hasReportError)}
              disabled={isGenerating || isLoading}
            />
            <Button
              label="Download PDF"
              variant="secondary"
              size="medium"
              icon={Download}
              onClick={handleDownload}
              disabled={!pdfUrl || isGenerating}
            />
          </div>
        </div>

        {loadError ? (
          <div className="dpr-one-page-banner dpr-one-page-banner--warn">
            {loadError}
            {pdfUrl ? (
              <button type="button" className="dpr-one-page-banner-link" onClick={handleOpenPdf}>
                Open saved PDF
              </button>
            ) : null}
          </div>
        ) : null}

        {isLoading ? (
          <div className="dpr-one-page-loading">
            <Spinner variant="inline" size="medium" />
            <span>Loading one-page report…</span>
          </div>
        ) : null}

        {!isLoading && viewModel && hasRenderableSections ? (
          <div className="op-doc">
            <HeaderSection header={header} project={selectedProject} />
            <MetaSection
              meta={meta}
              project={selectedProject}
              selectedPipeline={selectedPipeline}
              dprProjectStatus={dprProjectStatus || dprLogData?.dprProjectStatus}
            />
            <ProgressSection progress={progress} meta={meta} />
            <QualitySection quality={quality} />
            <SafetySection safety={safety} />
            <DowntimeSection downtime={downtime} />

            <ManpowerSection manpower={manpower} showOffshorePobShiftGrid={isOffshoreProject(selectedProject)} />
            <CriticalConsumablesSection criticalConsumables={criticalConsumables} />

            <CommentsSection comments={comments} />
            <SignaturesSection
              signatures={signatures}
              hideSubContractor={isOffshoreProject(selectedProject)}
            />
          </div>
        ) : null}

        {!isLoading && (!viewModel || !hasRenderableSections) && !loadError ? (
          <div className="dpr-one-page-loading">
            <p>No preview available yet.</p>
            <Button
              label="Generate PDF"
              variant="primary"
              size="medium"
              onClick={() => handleGenerate(false)}
              disabled={isGenerating}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DPROnePageDashboard;
